import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { FaArrowAltCircleRight, FaArrowAltCircleLeft } from 'react-icons/fa'

interface MenuConcept {
  id: string
  name: string
  chef: {
    id: string
    name: string
  }
  hasMenuItemWithMods: boolean
  cuisines: string[]
}

interface PickMenuModalProps {
  date: moment.Moment
  headquarter: number
  searchAvailableChefs: (val: {
    clientSetUpTime: moment.Moment
    headquarterId: number
  }) => Promise<string[]>
  searchMenuConcepts: (
    search: string,
    page: number,
    limit: number,
    chefIds: string[],
  ) => Promise<MenuConcept[]>
  onPickMenu: (menuConcept: MenuConcept) => void
}

const PickMenuModal: React.FC<PickMenuModalProps> = ({
  headquarter,
  date,
  onPickMenu,
  searchAvailableChefs,
  searchMenuConcepts,
}) => {
  const [availableChefs, setAvailableChefs] = useState<string[]>([])
  const [search, setSearch] = useState('')
  const [searchChanged, setSearchChanged] = useState(false)
  const [menuConcepts, setMenuConcepts] = useState<MenuConcept[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [page, setPage] = useState(0)
  const limit = 10

  useEffect(() => {
    fetchAvailableChefs()
  }, [date, headquarter])

  useEffect(() => {
    handleSearch()
  }, [page])

  const fetchAvailableChefs = async () => {
    const chefIds = await searchAvailableChefs({
      clientSetUpTime: date,
      headquarterId: headquarter,
    })
    setAvailableChefs(chefIds)
  }

  const handleSearch = async () => {
    if (!availableChefs.length) {
      return
    }
    setIsLoading(true)
    const results = await searchMenuConcepts(
      search,
      page,
      limit,
      availableChefs,
    )
    if (results) {
      setMenuConcepts(results)
    } else {
      setMenuConcepts([])
    }
    setSearchChanged(false)
    setIsLoading(false)
  }

  const handleSearchSubmit = () => {
    if (page > 0 && searchChanged) {
      setPage(0)
    } else {
      handleSearch()
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center space-x-2">
        <input
          type="text"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setSearchChanged(true)
          }}
          placeholder="Search by menu name, chef, or cuisine"
          className="border border-gray-300 rounded-md shadow-sm p-2 w-full focus:ring-indigo-500 focus:border-indigo-500"
          onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit()}
        />
        <button
          onClick={handleSearchSubmit}
          disabled={isLoading}
          className="px-4 py-2 bg-indigo-600 text-white font-semibold rounded-md hover:bg-indigo-700 disabled:bg-gray-400"
        >
          {isLoading ? 'Searching...' : 'Search'}
        </button>
      </div>
      <div className="flex flew-row items-center">
        <div className="w-1/3 flex flew-row items-center">
          {page > 0 && (
            <button
              className="bg-transparent border-none text-gray-700 pr-5 h-10"
              disabled={page <= 0}
              onClick={() => setPage(page - 1)}
            >
              <FaArrowAltCircleLeft size={20} />
            </button>
          )}
          <p>Page {page + 1}</p>
          {menuConcepts.length >= limit && (
            <button
              className="bg-transparent border-none text-gray-700 pl-5 h-10"
              onClick={() => setPage(page + 1)}
            >
              <FaArrowAltCircleRight size={20} />
            </button>
          )}
        </div>
        <div className="w-2/3 flex flex-row">
          {menuConcepts.length > 0 && (
            <p>Showing {menuConcepts.length} Menu Cards</p>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <div className="align-middle inline-block min-w-full">
          <div className="shadow overflow-hidden border border-gray-300 sm:rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-300">
                <tr className="divide-x divide-gray-200">
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider"
                  >
                    Name
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider"
                  >
                    Chef
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider"
                  >
                    Cuisines
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider"
                  >
                    Has Mods
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading && (
                  <tr>
                    <td colSpan={4} className="text-center py-4">
                      Loading...
                    </td>
                  </tr>
                )}
                {!isLoading && menuConcepts.length === 0 && (
                  <tr>
                    <td colSpan={4} className="text-center py-4 text-gray-900">
                      No menus found.
                    </td>
                  </tr>
                )}
                {menuConcepts.map((menu) => (
                  <tr
                    key={menu.id}
                    onClick={() => onPickMenu(menu)}
                    className="hover:bg-gray-100 cursor-pointer"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {menu.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {menu.chef.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {menu.cuisines.join(', ')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {menu.hasMenuItemWithMods ? 'Yes' : 'No'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PickMenuModal
